import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { ZodError } from 'zod';
import type { AccountPaymentsSuccessResponse } from '@libs/common-next';
import {
    BANNERS,
    useAlertBanner,
    useUserSession,
    logSentryError,
    SentryAcpCreditCardsErrors as SentryErrors,
} from '@libs/common-next';
// @ts-expect-error
import type { FormProps } from '@adyen/adyen-web/dist/types/utils/useForm/types';
import { CardDetailsInputs } from './CardDetailsInputs';
import type { ExperianResponseLayout } from '../ExperianModal';
import { ExperianModal, validateExperianAddressLayout } from '../ExperianModal';
import type { EditPaymentFormInterface } from './schema';
import { editPaymentFormSchema } from './schema';
import { BillingAddress } from '../BillingAddress';
import type { AddressFormTypeUSandCA } from '../AddressForm';
import { CARD_TOKENIZATION_REJECTED_MESSAGE } from './constants';
import { formatCreditCardExpirationDate, updatePayment, validateAddressForm } from './utils';
import { PROFILE_DATA } from '../../constants';
import { IsDefaultCheckbox } from './IsDefaultCheckbox';

const DEFAULT_EDIT_PAYMENT_FORM_STATE: EditPaymentFormInterface = {
    addressType: 'id',
    nameOnCard: '',
    expirationDate: '',
    isPrimary: false,
    selectedAddressId: '',
    cardId: 0,
    ccToken: '',
};

export function EditPaymentForm({
    isDefaultCheckDisabled,
    closeForm,
    defaultAddressId,
    editPaymentData,
}: {
    readonly isDefaultCheckDisabled: boolean;
    readonly editPaymentData: AccountPaymentsSuccessResponse;
    readonly closeForm: () => void;
    readonly defaultAddressId?: string;
}): ReactElement {
    const { setAlert } = useAlertBanner();
    const { updateProfileData } = useUserSession();

    const [isExperianLoading, setIsExperianLoading] = useState(false);
    const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const paymentFormState: EditPaymentFormInterface = {
        ...DEFAULT_EDIT_PAYMENT_FORM_STATE,
        addressType: 'id',
        selectedAddressId: editPaymentData.addressId.toString(),
        nameOnCard: editPaymentData.fullName,
        expirationDate: formatCreditCardExpirationDate(editPaymentData.expiration),
        isPrimary: editPaymentData.isPrimary,
        cardId: editPaymentData.cardId,
        ccToken: editPaymentData.ccToken,
    };

    const validate = async (values: EditPaymentFormInterface) => {
        let errors: FormikErrors<EditPaymentFormInterface> = {};

        try {
            editPaymentFormSchema.parse(values);
        } catch (error) {
            if (error instanceof ZodError) {
                errors = error.formErrors.fieldErrors;
            }
        }

        if (values.address) {
            const addressErrors = await validateAddressForm(values.address);

            if (addressErrors) {
                errors.address = addressErrors as FormikErrors<AddressFormTypeUSandCA>;
            }
        }

        return Object.keys(errors) ? errors : true;
    };

    const handleErrorBanner = (errors: FormikErrors<EditPaymentFormInterface>) => {
        const hasFormErrors = Object.keys(errors).length > 0;

        if (hasFormErrors) {
            setAlert({ ...BANNERS.invalidPaymentMethodFormik });

            document
                .querySelector<HTMLInputElement>(`[name="${Object.keys(errors)[0]}"]`)
                ?.focus({ preventScroll: true });
        }
    };

    /** Final function will be invoked after the experian address validation
     * if there are modals opened, will be invoked after clicking CTA on the modal
     * if there are no modals opened, will be called at once with Save cta click on a modal
     * */
    const handleSubmitForm = async (values: EditPaymentFormInterface) => {
        setAlert(null);
        setIsSubmitting(true);

        try {
            const response = await updatePayment(values);

            if (
                response &&
                'message' in response &&
                typeof response.message === 'string' &&
                response.message.includes(CARD_TOKENIZATION_REJECTED_MESSAGE)
            ) {
                setAlert({ ...BANNERS.invalidPaymentMethodAdyen });
            } else if (response && 'payment' in response) {
                await updateProfileData?.(PROFILE_DATA.PAYMENTS);

                closeForm();
                setAlert({ ...BANNERS.success, skipScrollToAlert: true });

                await updateProfileData?.(PROFILE_DATA.ADDRESSES);

                return;
            }
        } catch (error) {
            logSentryError(error, SentryErrors.EditPaymentForm.SubmitFailed);
        } finally {
            setIsSubmitting(false);
        }
    };

    /** Should be called on Save CTA click */
    const validateExperianOnSubmit = async (values: EditPaymentFormInterface) => {
        setAlert(null);
        if (values.addressType === 'id') {
            handleSubmitForm(values);
        } else {
            const addressValues = values?.address;
            setIsExperianLoading(true);
            const experianResponseValue = await validateExperianAddressLayout(addressValues);

            setExperianResponse(experianResponseValue);
            setIsExperianLoading(false);
        }
    };

    return (
        <Formik initialValues={paymentFormState} validate={validate} onSubmit={validateExperianOnSubmit}>
            {formikProps => (
                <Form noValidate onSubmit={formikProps.handleSubmit} data-testid="billing-address-form">
                    <CardDetailsInputs cardData={editPaymentData} />

                    <ExperianModal
                        experianResponse={experianResponse}
                        saveSelectedAddress={(formProps: FormProps) =>
                            handleSubmitForm(formProps as EditPaymentFormInterface)
                        }
                        loading={isExperianLoading}
                        setIsLoading={setIsExperianLoading}
                    />

                    <IsDefaultCheckbox disabled={isDefaultCheckDisabled} />

                    <BillingAddress
                        onCancel={closeForm}
                        defaultAddressId={defaultAddressId}
                        isLoading={isExperianLoading || isSubmitting}
                        handleErrorBanner={handleErrorBanner}
                    />
                </Form>
            )}
        </Formik>
    );
}
